/**
 * Serviço para operações relacionadas a equipamentos
 * Migrado de App.js para estrutura modular
 */

// Desenvolvimento: localhost:3002. Produção: mesma origem (Apache proxy encaminha /api)
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3002';
  }
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Busca equipamentos por série/patrimônio
 * @param {string} serial - Série ou patrimônio (mínimo 4 caracteres)
 * @param {string} type - Tipo de OS ('contrato' ou 'estoque')
 * @param {boolean} all - Se true, retorna todos os resultados
 * @returns {Promise<{success: boolean, suggestions: Array}>}
 */
export const searchEquipment = async (serial, type, all = false) => {
  try {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/api/equipment/search?serial=${encodeURIComponent(serial)}&type=${type}&all=${all}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Erro ao buscar equipamento');
    }
    
    const data = await response.json();
    return {
      success: true,
      suggestions: data.suggestions || []
    };
  } catch (error) {
    console.error('Erro ao buscar equipamento:', error);
    return {
      success: false,
      suggestions: [],
      error: error.message
    };
  }
};

/**
 * Busca última OS de um equipamento
 * @param {string} serie - Número de série do equipamento
 * @returns {Promise<{success: boolean, lastOsDate: string|null, lastOsData: object|null}>}
 */
export const getLastOS = async (serie) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/equipment/last-os?serie=${encodeURIComponent(serie)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Erro ao buscar última OS');
    }
    
    const data = await response.json();
    return {
      success: true,
      lastOsDate: data.lastOsDate || null,
      lastOsData: data.lastOsData || null
    };
  } catch (error) {
    console.error('Erro ao buscar última OS:', error);
    return {
      success: false,
      lastOsDate: null,
      lastOsData: null,
      error: error.message
    };
  }
};

/**
 * Busca peças pendentes da última OS
 * @param {string} osNumber - Número da OS
 * @returns {Promise<{success: boolean, items: Array}>}
 */
export const getLastOSParts = async (osNumber) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/equipment/last-os-parts?os=${encodeURIComponent(osNumber)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Erro ao buscar peças pendentes');
    }
    
    const data = await response.json();
    return {
      success: data.success || false,
      items: data.items || []
    };
  } catch (error) {
    console.error('Erro ao buscar peças pendentes da OS:', error);
    return {
      success: false,
      items: [],
      error: error.message
    };
  }
};

/**
 * Busca peças trocadas da última OS
 * @param {string} osNumber - Número da OS
 * @returns {Promise<{success: boolean, items: Array}>}
 */
export const getLastOSPartsChanged = async (osNumber) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/equipment/last-os-parts-changed?os=${encodeURIComponent(osNumber)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Erro ao buscar peças trocadas');
    }
    
    const data = await response.json();
    return {
      success: data.success || false,
      items: data.items || []
    };
  } catch (error) {
    console.error('Erro ao buscar peças trocadas da OS:', error);
    return {
      success: false,
      items: [],
      error: error.message
    };
  }
};

/**
 * Verifica se existe OS aberta para um equipamento
 * @param {string} serie - Número de série do equipamento
 * @returns {Promise<{success: boolean, hasOpenOs: boolean, osNumbers: Array}>}
 */
export const checkOpenOS = async (serie) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/equipment/check-open-os?serie=${encodeURIComponent(serie)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Erro ao verificar OS aberta');
    }
    
    const result = await response.json();
    return {
      success: result.success || false,
      hasOpenOs: result.hasOpenOs || false,
      osNumbers: result.osNumbers || [],
      recurrence: result.recurrence || null
    };
  } catch (error) {
    console.error('Erro ao verificar OS aberta:', error);
    return {
      success: false,
      hasOpenOs: false,
      osNumbers: [],
      error: error.message
    };
  }
};

/**
 * Retirada ativa em andamento para o equipamento (pedido não finalizado).
 */
export const checkRetiradaAtivaAbertura = async (serie) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(
      `${API_BASE_URL}/api/equipment/check-retirada-ativa-abertura?serie=${encodeURIComponent(serie)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Erro ao verificar retirada ativa');
    }
    const result = await response.json();
    return {
      success: result.success || false,
      hasRetiradaAtiva: result.hasRetiradaAtiva || false,
      codigos: result.codigos || []
    };
  } catch (error) {
    console.error('Erro ao verificar retirada ativa:', error);
    return {
      success: false,
      hasRetiradaAtiva: false,
      codigos: [],
      error: error.message
    };
  }
};

/**
 * Busca contratos por cliente/nome
 * @param {string} search - Termo de busca (mínimo 4 caracteres)
 * @returns {Promise<{success: boolean, suggestions: Array}>}
 */
export const searchContracts = async (search) => {
  try {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/api/equipment/contracts/search?search=${encodeURIComponent(search)}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Erro ao buscar contratos');
    }
    
    const data = await response.json();
    return {
      success: true,
      suggestions: data.suggestions || []
    };
  } catch (error) {
    console.error('Erro ao buscar contratos:', error);
    return {
      success: false,
      suggestions: [],
      error: error.message
    };
  }
};

/**
 * Verifica OS abertas por tipo para múltiplas séries
 * @param {Array<string>} series - Array de números de série
 * @param {string} tipoOs - Tipo de OS ('N', 'S', 'A', 'D')
 * @returns {Promise<{success: boolean, osAbertas: Array}>}
 */
export const checkOpenOSByType = async (series, tipoOs) => {
  try {
    const token = localStorage.getItem('authToken');
    const seriesParam = Array.isArray(series) ? series.join(',') : series;
    const url = `${API_BASE_URL}/api/equipment/check-open-os-by-type?series=${encodeURIComponent(seriesParam)}&tipoOs=${encodeURIComponent(tipoOs)}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Erro ao verificar OS abertas');
    }
    
    const data = await response.json();
    return {
      success: true,
      osAbertas: data.osAbertas || []
    };
  } catch (error) {
    console.error('Erro ao verificar OS abertas por tipo:', error);
    return {
      success: false,
      osAbertas: [],
      error: error.message
    };
  }
};

/**
 * Busca equipamentos de um contrato
 * @param {string} contrato - Código do contrato (TB02111_CODIGO)
 * @returns {Promise<{success: boolean, equipment: Array}>}
 */
export const getContractEquipment = async (contrato) => {
  try {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/api/equipment/contract-equipment?contrato=${encodeURIComponent(contrato)}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Erro ao buscar equipamentos do contrato');
    }
    
    const data = await response.json();
    return {
      success: true,
      equipment: data.equipment || []
    };
  } catch (error) {
    console.error('Erro ao buscar equipamentos do contrato:', error);
    return {
      success: false,
      equipment: [],
      error: error.message
    };
  }
};

/**
 * Busca contadores do equipamento
 * @param {string} serie - Número de série do equipamento
 * @returns {Promise<{success: boolean, ContadorPB: number, ContadorCor: number, ContadorTotal: number}>}
 */
export const getCounters = async (serie) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/equipment/counters?serie=${encodeURIComponent(serie)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Erro ao buscar contadores');
    }
    
    const data = await response.json();
    return {
      success: true,
      ContadorPB: data.ContadorPB || 0,
      ContadorCor: data.ContadorCor || 0,
      ContadorTotal: data.ContadorTotal || 0
    };
  } catch (error) {
    console.error('Erro ao buscar contadores do equipamento:', error);
    return {
      success: false,
      ContadorPB: 0,
      ContadorCor: 0,
      ContadorTotal: 0,
      error: error.message
    };
  }
};

/**
 * Busca um campo do TB02112 por serie + contrato.
 * field: 'SOLICITANTE' | 'CONTATO' | 'EMAIL'
 */
export const getTB02112Field = async (serie, contrato, field) => {
  try {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/api/equipment/tb02112-field?serie=${encodeURIComponent(serie)}&contrato=${encodeURIComponent(contrato)}&field=${encodeURIComponent(field)}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Erro ao buscar TB02112');
    }

    const data = await response.json();
    return {
      success: true,
      value: data?.value ?? null
    };
  } catch (error) {
    console.error('Erro ao buscar TB02112:', error);
    return {
      success: false,
      value: null,
      error: error.message
    };
  }
};

